/**
 * Owner: Masa Bayu
 * File: marketplace-labels.ts
 * Date:Time: 2026-10-18 19:40
 * Description: Marketplace locale §377 — interface MarketplaceLabels + jadual
 *   label EN & MS (dipindah verbatim dari marketplace-locale.ts; data sama).
 */

import type { MarketplacePriceBand, MarketplaceSortId } from './marketplace-projection';
import type { MarketplaceCurrency, MarketplaceLanguage } from './marketplace-locale';
import type { LandingIconName } from './landing/LandingIcons';
import type { QxLanguage } from '@qx/qxread-core';

export interface MarketplaceLabels {
  readonly nav: {
    readonly home: string;
    readonly books: string;
    readonly authors: string;
    readonly categories: string;
    readonly about: string;
    readonly ariaLabel: string;
    readonly searchIcon: string;
    readonly accountFallback: string;
    readonly logInRegister: string;
  };
  readonly hero: {
    readonly title: string;
  };
  readonly banner: {
    readonly carouselLabel: string;
    readonly previous: string;
    readonly next: string;
    readonly slidesLabel: string;
    readonly altPrefix: string;
  };
  readonly sidebar: {
    readonly ariaLabel: string;
    readonly searchLabel: string;
    readonly searchPlaceholder: string;
    readonly categoryLegend: string;
    readonly allCategories: string;
    readonly moreCategories: string;
    readonly subcategoryLegend: string;
    readonly allSubcategories: string;
    readonly allPrices: string;
    readonly priceLegend: string;
  };
  /** §405 — bahasa jadual; nama taksonomi QX dijana qxCategoryName(id, language). */
  readonly language: QxLanguage;
  readonly priceBands: Readonly<Record<MarketplacePriceBand, string>>;
  readonly sorts: Readonly<Record<MarketplaceSortId, string>>;
  readonly results: {
    readonly ariaLabel: string;
    readonly sortBy: string;
    readonly emptyTitle: string;
    readonly emptySub: string;
    readonly resetFilters: string;
  };
  /** §405 — pelayar kategori QX dua aras (/categories). */
  readonly categoryBrowser: {
    readonly heading: string;
    readonly intro: string;
    readonly browseAll: string;
    readonly expandAria: string;
    readonly subHeading: string;
  };
  readonly card: {
    readonly gridAriaLabel: string;
    readonly buyNow: string;
    readonly inCart: string;
    /** §466 S1 — harga tiada dalam descriptor: paparkan status, jangan 0.00. */
    readonly priceUnknown: string;
  };
  readonly auth: {
    readonly titleLogin: string;
    readonly subtitle: string;
    readonly emailLabel: string;
    readonly emailPlaceholder: string;
    readonly passwordLabel: string;
    readonly passwordPlaceholder: string;
    readonly submitLogin: string;
    readonly goCreateAccount: string;
    readonly errorCredentials: string;
    readonly errorInvalid: string;
    readonly errorUnavailable: string;
    readonly paymentUnavailable: string;
    readonly busy: string;
    readonly close: string;
  };
  readonly detail: {
    readonly back: string;
    readonly addToCart: string;
    readonly descriptionFallback: string;
    readonly moreLikeThis: string;
    readonly notFoundTitle: string;
    readonly notFoundSub: string;
    readonly backHome: string;
    readonly byPrefix: string;
    readonly buyEbook: string;
    readonly readSample: string;
    readonly share: string;
    readonly linkCopied: string;
    readonly aboutTitle: string;
    readonly detailsTitle: string;
    readonly authorTitle: string;
    readonly reviewsTitle: string;
    readonly authorProfileLink: string;
    readonly reviewsEmpty: string;
    readonly languageNames: Readonly<Record<MarketplaceLanguage, string>>;
    readonly detailLabels: {
      readonly author: string;
      readonly language: string;
      readonly category: string;
      readonly published: string;
    };
  };
  readonly pagination: {
    readonly ariaLabel: string;
    readonly previous: string;
    readonly next: string;
  };
  readonly footer: {
    readonly ariaLabel: string;
    readonly trust: Readonly<
      { readonly icon: LandingIconName; readonly title: string; readonly sub: string }[]
    >;
  };
}

export function priceBandTable(
  currency: MarketplaceCurrency,
  freeLabel: string,
): Readonly<Record<MarketplacePriceBand, string>> {
  const prefix = currency === 'MYR' ? 'RM ' : '$';
  return Object.freeze({
    free: freeLabel,
    b1_10: `${prefix}1 - 10`,
    b11_30: `${prefix}11 - 30`,
    b31_50: `${prefix}31 - 50`,
    b51_plus: `${prefix}51+`,
  });
}

export const EN_LABELS: MarketplaceLabels = Object.freeze({
  nav: Object.freeze({
    home: 'Home',
    books: 'Books',
    authors: 'Authors',
    categories: 'Categories',
    about: 'About Us',
    ariaLabel: 'Primary',
    searchIcon: 'Search books',
    accountFallback: 'Account',
    logInRegister: 'Log In / Register',
  }),
  hero: Object.freeze({
    title: 'Book List',
  }),
  banner: Object.freeze({
    carouselLabel: 'Featured banners',
    previous: 'Previous banner',
    next: 'Next banner',
    slidesLabel: 'Banner slides',
    altPrefix: 'QXRead featured banner',
  }),
  sidebar: Object.freeze({
    ariaLabel: 'Book filters',
    searchLabel: 'Search books, authors, or keywords',
    searchPlaceholder: 'Search books, authors, or keywords…',
    categoryLegend: 'Category',
    allCategories: 'All Categories',
    moreCategories: 'More',
    subcategoryLegend: 'Subcategory',
    allSubcategories: 'All Subcategories',
    allPrices: 'All Prices',
    priceLegend: 'Price',
  }),
  language: 'en',
  priceBands: priceBandTable('USD', 'Free'),
  sorts: Object.freeze({
    newest: 'Newest',
    price_low: 'Lowest Price',
    price_high: 'Highest Price',
    title_a_z: 'Title A–Z',
  }),
  results: Object.freeze({
    ariaLabel: 'Book list results',
    sortBy: 'Sort:',
    emptyTitle: 'No books match your filters.',
    emptySub: 'Try a different keyword or reset your filters.',
    resetFilters: 'Reset Filters',
  }),
  categoryBrowser: Object.freeze({
    heading: 'Browse Categories',
    intro: 'Pick a category to see its subcategories, or browse everything in one tap.',
    browseAll: 'Browse all',
    expandAria: 'Show subcategories',
    subHeading: 'Subcategories',
  }),
  card: Object.freeze({
    gridAriaLabel: 'Book list',
    buyNow: 'Buy Now',
    inCart: 'In Cart',
    priceUnknown: 'Price not set',
  }),
  auth: Object.freeze({
    titleLogin: 'Sign in to continue',
    subtitle: 'Sign in to buy this eBook — your purchases are tied to your account.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Your password',
    submitLogin: 'Sign In',
    goCreateAccount: "Don't have an account? Create one",
    errorCredentials: 'Email or password is incorrect.',
    errorInvalid: 'Please check the email and password (minimum 8 characters).',
    errorUnavailable: 'Account service is unavailable right now. Please try again later.',
    paymentUnavailable: 'Payment is unavailable right now. Please try again later.',
    busy: 'Please wait…',
    close: 'Close',
  }),
  detail: Object.freeze({
    back: 'Back to Book List',
    addToCart: 'Add to Cart',
    descriptionFallback: 'No description has been published for this title yet.',
    moreLikeThis: 'More like this',
    notFoundTitle: 'Book not found',
    notFoundSub: 'This catalog reference is not available in the marketplace.',
    backHome: 'Back to Book List',
    byPrefix: 'by',
    buyEbook: 'Buy eBook',
    readSample: 'Read Sample',
    share: 'Share',
    linkCopied: 'Link copied',
    aboutTitle: 'About This eBook',
    detailsTitle: 'eBook Details',
    authorTitle: 'About the Author',
    reviewsTitle: 'Reader Reviews',
    authorProfileLink: 'View author profile',
    reviewsEmpty: 'No reader reviews have been published for this title yet.',
    languageNames: Object.freeze({ en: 'English', ms: 'Malay' }),
    detailLabels: Object.freeze({
      author: 'Author',
      language: 'Language',
      category: 'Category',
      published: 'Published',
    }),
  }),
  pagination: Object.freeze({
    ariaLabel: 'Book list pages',
    previous: 'Previous page',
    next: 'Next page',
  }),
  footer: Object.freeze({
    ariaLabel: 'About QX eBook Marketplace',
    trust: Object.freeze([
      Object.freeze({
        icon: 'book',
        title: '100% eBooks',
        sub: 'Read inside QXReader — nothing to download',
      }),
      Object.freeze({
        icon: 'shield',
        title: 'Secure Payments',
        sub: 'Safe and trusted transactions',
      }),
      Object.freeze({
        icon: 'headset',
        title: 'Customer Support',
        sub: 'We are always ready to help',
      }),
    ]),
  }),
});

export const MS_LABELS: MarketplaceLabels = Object.freeze({
  nav: Object.freeze({
    home: 'Laman Utama',
    books: 'Buku',
    authors: 'Penulis',
    categories: 'Kategori',
    about: 'Tentang Kami',
    ariaLabel: 'Utama',
    searchIcon: 'Cari buku',
    accountFallback: 'Akaun',
    logInRegister: 'Log Masuk / Daftar',
  }),
  hero: Object.freeze({
    title: 'Senarai Buku',
  }),
  banner: Object.freeze({
    carouselLabel: 'Sepandu utama',
    previous: 'Sepandu sebelumnya',
    next: 'Sepandu seterusnya',
    slidesLabel: 'Slaid sepandu',
    altPrefix: 'Sepandu utama QXRead',
  }),
  sidebar: Object.freeze({
    ariaLabel: 'Penuras buku',
    searchLabel: 'Cari buku, penulis, atau kata kunci',
    searchPlaceholder: 'Cari buku, penulis, atau kata kunci…',
    categoryLegend: 'Kategori',
    allCategories: 'Semua Kategori',
    moreCategories: 'Lain-lain',
    subcategoryLegend: 'Subkategori',
    allSubcategories: 'Semua Subkategori',
    allPrices: 'Semua Harga',
    priceLegend: 'Harga',
  }),
  language: 'ms',
  priceBands: priceBandTable('MYR', 'Percuma'),
  sorts: Object.freeze({
    newest: 'Terbaharu',
    price_low: 'Termurah',
    price_high: 'Termahal',
    title_a_z: 'Tajuk A–Z',
  }),
  results: Object.freeze({
    ariaLabel: 'Keputusan senarai buku',
    sortBy: 'Urutkan:',
    emptyTitle: 'Tiada buku sepadan dengan penuras anda.',
    emptySub: 'Cuba kata kunci lain atau tetapkan semula penuras.',
    resetFilters: 'Tetapkan Semula Penuras',
  }),
  categoryBrowser: Object.freeze({
    heading: 'Terokai Kategori',
    intro: 'Pilih kategori untuk melihat subkategori, atau lihat semua dengan satu ketikan.',
    browseAll: 'Lihat semua',
    expandAria: 'Tunjuk subkategori',
    subHeading: 'Subkategori',
  }),
  card: Object.freeze({
    gridAriaLabel: 'Senarai buku',
    buyNow: 'Beli Sekarang',
    inCart: 'Dalam Troli',
    priceUnknown: 'Harga belum ditetapkan',
  }),
  auth: Object.freeze({
    titleLogin: 'Daftar masuk untuk meneruskan',
    subtitle: 'Daftar masuk untuk membeli eBook ini — pembelian anda dikaitkan dengan akaun anda.',
    emailLabel: 'E-mel',
    emailPlaceholder: 'anda@contoh.com',
    passwordLabel: 'Kata Laluan',
    passwordPlaceholder: 'Kata laluan anda',
    submitLogin: 'Daftar Masuk',
    goCreateAccount: 'Tiada akaun? Cipta satu',
    errorCredentials: 'E-mel atau kata laluan tidak tepat.',
    errorInvalid: 'Sila semak e-mel dan kata laluan (minimum 8 aksara).',
    errorUnavailable: 'Perkhidmatan akaun tidak tersedia buat masa ini. Sila cuba sebentar lagi.',
    paymentUnavailable: 'Pembayaran tidak tersedia buat masa ini. Sila cuba sebentar lagi.',
    busy: 'Sila tunggu…',
    close: 'Tutup',
  }),
  detail: Object.freeze({
    back: 'Kembali ke Senarai Buku',
    addToCart: 'Tambah ke Troli',
    descriptionFallback: 'Tiada sinopsis diterbitkan untuk tajuk ini lagi.',
    moreLikeThis: 'Lebih Seperti Ini',
    notFoundTitle: 'Buku Tidak Dijumpai',
    notFoundSub: 'Rujukan katalog ini tidak tersedia dalam marketplace.',
    backHome: 'Kembali ke Senarai Buku',
    byPrefix: 'oleh',
    buyEbook: 'Beli eBook',
    readSample: 'Baca Sampel',
    share: 'Kongsi',
    linkCopied: 'Pautan disalin',
    aboutTitle: 'Perihal eBook Ini',
    detailsTitle: 'Butiran eBook',
    authorTitle: 'Perihal Penulis',
    reviewsTitle: 'Ulasan Pembaca',
    authorProfileLink: 'Lihat profil penulis',
    reviewsEmpty: 'Tiada ulasan pembaca diterbitkan untuk tajuk ini lagi.',
    languageNames: Object.freeze({ en: 'Bahasa Inggeris', ms: 'Bahasa Melayu' }),
    detailLabels: Object.freeze({
      author: 'Penulis',
      language: 'Bahasa',
      category: 'Kategori',
      published: 'Diterbitkan',
    }),
  }),
  pagination: Object.freeze({
    ariaLabel: 'Muka surat senarai buku',
    previous: 'Muka surat sebelumnya',
    next: 'Muka surat berikutnya',
  }),
  footer: Object.freeze({
    ariaLabel: 'Tentang QX eBook Marketplace',
    trust: Object.freeze([
      Object.freeze({
        icon: 'book',
        title: '100% eBook',
        sub: 'Baca dalam QXReader — tiada fail untuk dimuat turun',
      }),
      Object.freeze({
        icon: 'shield',
        title: 'Pembayaran Selamat',
        sub: 'Transaksi yang selamat dan dipercayai',
      }),
      Object.freeze({
        icon: 'headset',
        title: 'Sokongan Pelanggan',
        sub: 'Kami sentiasa sedia membantu',
      }),
    ]),
  }),
});
